import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, ShieldCheck, Users, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

export default function LoginPage() {
  const { login, formatErr } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("citizen");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const quickFill = (e, p) => { setEmail(e); setPassword(p); };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await login(email, password);
      nav(sp.get("next") || "/app");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden hero-punjab text-white">
        <div className="absolute inset-0 grain pointer-events-none"/>
        <div className="relative p-12 flex flex-col h-full">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[hsl(var(--primary))] text-white font-bold text-[10px]">PEDA</div>
            <div>
              <div className="font-heading font-semibold text-sm">PEDA Portal</div>
              <div className="text-[10px] opacity-70">Government of Punjab</div>
            </div>
          </Link>
          <div className="mt-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]"/> Secure sign-in · JWT + RBAC
            </div>
            <h1 className="mt-4 text-4xl font-heading font-bold">Welcome back.</h1>
            <p className="mt-3 max-w-md text-white/80">Access citizen services, projects, GIS dashboards, ERP, and AI tools — all from one unified portal.</p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              <Perk icon={ShieldCheck} title="Secure" v="RBAC · Audit"/>
              <Perk icon={Users} title="Unified" v="12+ roles"/>
              <Perk icon={LogIn} title="Fast" v="SSO ready"/>
            </div>
          </div>
        </div>
      </div>
      <div className="grid place-items-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-heading font-bold">Sign in</h2>
          <p className="text-muted-foreground mt-1 text-sm">Use your PEDA credentials.</p>

          <div className="mt-6 flex rounded-full border border-border p-1 bg-muted/50 text-sm">
            {["citizen","employee","admin"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`flex-1 px-3 py-1.5 rounded-full capitalize ${tab === t ? "bg-background shadow" : ""}`} data-testid={`tab-${t}`}>
                {t}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Email</label>
              <input data-testid={AUTH.loginEmail} value={email} onChange={(e)=>setEmail(e.target.value)} required type="email" className="input mt-1" placeholder="you@peda.gov.in"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Password</label>
              <input data-testid={AUTH.loginPassword} value={password} onChange={(e)=>setPassword(e.target.value)} required type="password" className="input mt-1" placeholder="••••••••"/>
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <button data-testid={AUTH.loginSubmit} type="submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 rounded-xl border border-dashed border-border p-4">
            <div className="label-tiny mb-2">Demo credentials</div>
            <div className="grid grid-cols-1 gap-1.5 text-xs">
              {tab === "admin" && (
                <Demo onClick={()=>quickFill("admin@peda.gov.in","Admin@Peda2026")} email="admin@peda.gov.in" role="Super Admin"/>
              )}
              {tab === "employee" && (
                <>
                  <Demo onClick={()=>quickFill("ceo@peda.gov.in","Ceo@Peda2026")} email="ceo@peda.gov.in" role="CEO"/>
                  <Demo onClick={()=>quickFill("director@peda.gov.in","Director@Peda2026")} email="director@peda.gov.in" role="Director"/>
                  <Demo onClick={()=>quickFill("finance@peda.gov.in","Finance@Peda2026")} email="finance@peda.gov.in" role="Finance"/>
                  <Demo onClick={()=>quickFill("hr@peda.gov.in","HrPeda@2026")} email="hr@peda.gov.in" role="HR"/>
                  <Demo onClick={()=>quickFill("engineer@peda.gov.in","Engineer@Peda2026")} email="engineer@peda.gov.in" role="Engineer"/>
                  <Demo onClick={()=>quickFill("inspector@peda.gov.in","Inspect@Peda2026")} email="inspector@peda.gov.in" role="Inspector"/>
                </>
              )}
              {tab === "citizen" && (
                <Demo onClick={()=>quickFill("citizen@peda.gov.in","Citizen@Peda2026")} email="citizen@peda.gov.in" role="Citizen"/>
              )}
            </div>
          </div>

          <div className="mt-6 text-sm text-center text-muted-foreground">
            New user? <Link to="/register" className="text-[hsl(var(--primary))] font-semibold">Create citizen account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Demo({ onClick, email, role }) {
  return (
    <button type="button" onClick={onClick} className="text-left flex items-center justify-between rounded-md hover:bg-muted px-2 py-1.5">
      <span className="font-mono">{email}</span>
      <span className="text-muted-foreground">{role}</span>
    </button>
  );
}
function Perk({ icon: Icon, title, v }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <Icon className="h-4 w-4 text-[hsl(var(--accent))]"/>
      <div className="mt-2 text-xs font-semibold">{title}</div>
      <div className="text-[11px] text-white/60">{v}</div>
    </div>
  );
}
